import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";

import { Outlet } from "react-router-dom";
import SideBar from "./SideBar";
import UserPopup from "./UserPopup";
import { Segment } from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  CircularProgress,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import ConnectedAsBanner from "../admin/ConnectedAsBanner";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { toggleSideBar } from "../../store/reducers/display.reducer";
import RenewalBanner from "../admin/RenewalBanner";
import { TBilling } from "../../shared/types/billing.type";
import { get, throttle } from "lodash";
import DisabledPlatform from "./DisabledPlatform";
import { Icon } from "@iconify/react";
import SwitchPlatfromBanner from "../dashboard/SwitchPlatfromBanner";
import LanguageSelector from "../global/LanguageSelector";
import { languages } from "../../enums/languages.enum";
import { useLocales } from "../../hooks/useLocales";
import { useAdminMessage } from "../../hooks/useAdminMessage";
import { TUser } from "../../shared/types/user.type";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { formatDate } from "../../shared/utils/DateUtils";
import UserMessagesBanners from "../admin/UserMessagesBanners";
import { useCallback, useEffect, useRef } from "react";
import { ChukkersEditedBanner } from "../admin/ChukkersEditedBanner";
import { useAuth } from "../../hooks/useAuth";

dayjs.extend(timezone);

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  width: `calc(100% - ${open ? 280 : 70}px)`,
  padding: theme.spacing(3),
  overflow: "hidden",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function Navbar({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem("token");
  const open = useSelector<RootState>(
    (state) => state.display.sideBarOpened,
  ) as boolean;
  const renewal_enabled = useSelector<RootState>(
    (state) => state.user.user?.renewal_banner_enabled,
  ) as boolean;
  const user = useSelector<RootState>((state) => state.user.user) as
    | TUser
    | undefined;
  const platform_disabled = useSelector<RootState>(
    (state) => state.user.user?.disable_platform,
  ) as boolean;
  const isLoading = useSelector<RootState>(
    (state) => state.user.isUserLoading,
  ) as boolean;
  const billings = useSelector<RootState>(
    (state) => state.user.user?.user_billing_infos,
  ) as TBilling[];
  const { updateLang, currentLang,updateLangTemp } = useLocales();
  const { messages, isMessagesLoading } = useAdminMessage({
    enableQuery: !isLoading,
    userId: user?.["@id"] ?? "",
    userDate: dayjs().utc(true).toISOString(),
  });
  const isMenuHidden = useSelector<RootState>(
    (state) => state.display.hideAllMenus,
  ) as boolean;
  const restricted = useSelector<RootState>(
    (state) => state.user.restrictedMode,
  ) as boolean;

  const isChukkerBannerVisible = useSelector<RootState>(
    (state) => state.display.isChukkerEditedBannerVisible,
  ) as boolean;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const mainContainer = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const appBarRef = useRef<HTMLDivElement | null>(null);
  const lastY = useRef(0);

  const stripe_id = get(billings, "[0].stripe_id", undefined);
  const link = stripe_id
    ? process.env.REACT_APP_BACKOFFICE_BILLING_URI?.concat(stripe_id ?? "")
    : "";

  const dispatch = useDispatch();

  const handleDrawerClose = () => {
    dispatch(toggleSideBar(false));
  };

  const scrollHandler = useCallback((event: Event) => {
    const scrollTop = mainContainer.current?.scrollTop || 0;

    if (scrollTop > lastY.current) {
      appBarRef.current?.classList.add("hide-up");
      messagesRef.current?.classList.add("hide-up");
      lastY.current = scrollTop;
    } else {
      lastY.current = scrollTop;
      appBarRef.current?.classList.remove("hide-up");
      messagesRef.current?.classList.remove("hide-up");
    }
  }, []);

  useEffect(() => {
    if (!appBarRef.current || !mainContainer.current) return;

    // If no longer small screen, remove the hide class to make navbar visible
    if (!isSmallScreen) {
      appBarRef.current.classList.remove("hide-up");
      return;
    }
    const throttledScroll = throttle(scrollHandler, 100);

    mainContainer.current.addEventListener("scroll", throttledScroll);

    return () => {
      mainContainer.current?.removeEventListener("scroll", throttledScroll);
      throttledScroll.cancel?.(); // cancel any pending throttled calls
    };
  }, [isSmallScreen]);

  const isTokenHasIu = React.useMemo(() => {
    if (!token) return false;
    //decode token
    try {
      let decoded_token = jwtDecode(token);
      return "iu" in decoded_token;
    } catch (error) {
      return false;
    }
  }, [token]);

  const changeSessionLang = useCallback((lang: string) => {
    updateLangTemp(lang, () => window.location.reload());
  }, []);

  return (
    <div className="app-bg">
      <Box
        sx={{
          display: "flex",
          position: "relative",
          overflow: "hidden",
          height: "100vh",
        }}
      >
        <DisabledPlatform link={link} open={platform_disabled} />
        <AppBar
          ref={appBarRef}
          position="fixed"
          sx={{
            background: "hsla(0, 0%, 10%,.4) !important",
            backdropFilter: "blur(20px) !important",
            borderBottom: ".5px solid rgba(145, 158, 171, 0.24)",
            height: "64px",
            justifyContent: "center",
            transition: "transform ease-in-out 0.3s",
            willChange: "transform",
            transitionDelay: 1,
            overflow: "hidden",
            WebkitBackdropFilter: "blur(20px) !important",
            width: `calc(100vw - ${open ? 290 : isMenuHidden || restricted ? 0 : 70}px)`,
          }}
          open={open}
          className="glass"
        >
          <Toolbar>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1 }}
            >
              {process.env.REACT_APP_NAME ?? "Equimetre"}
            </Typography>

            {/* Hide popup user when page loaded with app synch */}
            {!isMenuHidden && (
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <LanguageSelector
                  langs={languages}
                  onSelect={changeSessionLang}
                  currentLang={currentLang}
                  loading={false}
                />
                {!restricted && <UserPopup></UserPopup>}
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <SideBar
          open={open}
          onClose={handleDrawerClose}
          isMenuHidden={isMenuHidden || restricted}
        ></SideBar>

        <Main
          ref={mainContainer}
          open={open}
          sx={{
            position: "relative",
            p: 0,
            overflow: "auto", // Changed from 'hidden'
            height: "100vh",
            pt: "64px", // Compensate for AppBar
            width: "100%",
          }}
        >
          {/* Banner Section */}
          <Box
            className={"sticky-hideable"}
            ref={messagesRef}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1001,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {renewal_enabled && (
              <RenewalBanner stripe_id={stripe_id} open={open}></RenewalBanner>
            )}

            {isTokenHasIu && (
              <ConnectedAsBanner open={open}></ConnectedAsBanner>
            )}

            <UserMessagesBanners
              loading={isMessagesLoading}
              messages={messages}
            />
            {isChukkerBannerVisible && <ChukkersEditedBanner />}
          </Box>

          {/* render screens here */}
          <Box
            sx={{
              minHeight: "calc(100vh - 64px)",
              px: 2,
              pt: 2,
              pb: 4,
            }}
          >
            {isLoading ? (
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                height={"80vh"}
              >
                <CircularProgress />
              </Box>
            ) : (
              children
            )}
          </Box>
        </Main>
      </Box>
    </div>
  );
}
